import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { uploadFileToS3 } from './UploadFileToS3';
import { FaFilePdf } from 'react-icons/fa';

const ReservationsContainer = styled.div`
  display: flex;
  flex-direction: display;
  justify-content: center;
  align-items: center;
  padding: 0;
  background-color: #f9f9f9;
`;

const TableWrapper = styled.div`
  width: 100%;
  background-color: #fff;
  padding: 0;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
`;

const TableHeader = styled.th`
  background-color: #007bff;
  color: white;
  padding: 10px;
  border: 1px solid #ddd;const openInvoice = (invoiceUrl) => {
    // Open the invoice in a new tab
    if (invoiceUrl) {
      window.open(invoiceUrl, '_blank');
    }
  };
  width: ${(props) => props.width || 'auto'};
  cursor: pointer;
`;

const TableCell = styled.td`
  padding: 10px;
  border: 1px solid #ddd;
  text-align: center;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const PageButton = styled.button`
  background-color: #007bff;
  color: white;
  border: none;
  padding: 10px;
  margin: 0 5px;
  border-radius: 5px;
  cursor: pointer;

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background-color: #0056b3;
  }
`;

const RowsPerPageSelect = styled.select`
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
`;

const ErrorMessage = styled.p`
  color: red;
`;

const StatusButton = styled.button`
  background-color: #28a745;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 5px;
  cursor: pointer;

  &:hover {
    background-color: #218838;
  }
`;

const ModalContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Modal = styled.div`
  background-color: #fff;
  padding: 20px;
  border-radius: 10px;
  width: 400px;
  text-align: center;
`;

const FileInput = styled.input`
  margin-top: 0px;
`;

const InvoiceButton = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

// Helper function to upload images to S3
const uploadImageToS3 = async (reservationId, imageFile) => {
  const s3Bucket = 'room2w-images'; // Replace with your S3 bucket name
  const region = 'eu-west-1'; // Your S3 bucket region
  const fileName = `proofOfPayment/${reservationId}/${imageFile.name}`;
  const s3Url = `https://${s3Bucket}.s3.${region}.amazonaws.com/${fileName}`;

  // Assuming you have a function that handles S3 upload
  await uploadFileToS3(imageFile, fileName); // Implement this function to upload file to S3

  return s3Url;
};

const MyReservations = ({ authToken }) => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [reservationsPerPage, setReservationsPerPage] = useState(5);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setSelectedFile(file);
  };

  const handleStatusChange = async () => {
    if (!selectedFile) {
      return; // Do nothing if no file is selected
    }

    setUploading(true);

    try {
      const uploadedUrl = await uploadImageToS3(selectedReservation.guid, selectedFile);

      const response = await fetch(`http://api.aidaproduction.com/update/${selectedReservation.guid}`, {
      // const response = await fetch(`http://localhost:8000/update/${selectedReservation.guid}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          payment_proof_urls: uploadedUrl,
          payment_confirmed: true, // Marking payment as confirmed
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update reservation.');
      }

      setStatusModalOpen(false);
    } catch (error) {
      setError('Error updating reservation: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const openModal = (reservation) => {
    setSelectedReservation(reservation);
    setStatusModalOpen(true);
  };

  const closeModal = () => {
    setStatusModalOpen(false);
  };

  const downloadInvoice = async (invoiceUrl) => {
    const response = await fetch(invoiceUrl);
    const blob = await response.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'invoice.pdf'; // You can customize the filename here
    link.click();
  };

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/my-reservations`, {
    // fetch('http://localhost:8000/my-reservations', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to fetch reservations.');
        }
        return response.json();
      })
      .then((data) => {
        setReservations(data || []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [authToken]);

  const handleSort = (field) => {
    const direction = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(direction);

    const sortedReservations = [...reservations].sort((a, b) => {
      if (direction === 'asc') {
        return a[field] > b[field] ? 1 : -1;
      } else {
        return a[field] < b[field] ? 1 : -1;
      }
    });
    setReservations(sortedReservations);
  };

  const indexOfLastReservation = currentPage * reservationsPerPage;
  const indexOfFirstReservation = indexOfLastReservation - reservationsPerPage;
  const currentReservations = reservations.slice(indexOfFirstReservation, indexOfLastReservation);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return <p>Loading...</p>;
  }

  if (error) {
    return <ErrorMessage>{error}</ErrorMessage>;
  }

  if (!reservations || reservations.length === 0) {
    return <p>No reservations available.</p>;
  }

  return (
    <ReservationsContainer>
      <TableWrapper>
        <h2>My Reservations</h2>
        <Table>
          <thead>
            <tr>
              <TableHeader width="25%" onClick={() => handleSort('name')}>Name</TableHeader>
              <TableHeader width="15%" onClick={() => handleSort('Location')}>Location</TableHeader>
              <TableHeader width="25%" onClick={() => handleSort('Dates')}>Dates</TableHeader>
              <TableHeader width="10%" onClick={() => handleSort('CheckIn')}>Check In</TableHeader>
              <TableHeader width="10%" onClick={() => handleSort('CheckOut')}>Check Out</TableHeader>
              <TableHeader width="10%" onClick={() => handleSort('Overnight')}>Price</TableHeader>
              <TableHeader width="10%" onClick={() => handleSort('price')}>Price</TableHeader>
              <TableHeader width="10%" onClick={() => handleSort('occupants')}>Occupants</TableHeader>
              <TableHeader width="10%" onClick={() => handleSort('status')}>Status</TableHeader>
              <TableHeader width="10%">Invoice</TableHeader>
            </tr>
          </thead>
          <tbody>
            {currentReservations.map((reservation, index) => (
              <tr key={index}>
                <TableCell>
                  <a href={`/listing/${reservation.listing_id}`}>{reservation.name}</a>
                </TableCell>
                <TableCell>{reservation.city + ", " + reservation.country}</TableCell>
                <TableCell>
                  {`${new Date(reservation.start_date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })} till ${new Date(reservation.end_date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}`}
                </TableCell>
                <TableCell>{reservation.checkin_time}</TableCell>
                <TableCell>{reservation.checkout_time}</TableCell>
                <TableCell>{reservation.full_day ? '✔' : '❌'}</TableCell>
                <TableCell>${reservation.total_payment}</TableCell>
                <TableCell>{reservation.number_of_occupants}</TableCell>
                <TableCell>
                <StatusButton
                  onClick={() => openModal(reservation)}
                  disabled={reservation.status !== 'Pending Payment'}
                  style={{
                    backgroundColor: reservation.status === 'Pending Payment Verification' ? '#28a745' : '#28a999',
                    
                  }}
                >
                  {reservation.status}
                </StatusButton>
                </TableCell>
                <TableCell>
                  {reservation.invoice_url && (
                    <InvoiceButton onClick={() => downloadInvoice(reservation.invoice_url)}>
                    <FaFilePdf size={20} color="#007bff" />
                   </InvoiceButton>

                  )}
                </TableCell>
              </tr>
            ))}
          </tbody>
        </Table>

        <PaginationContainer>
          <div>
            <span>Rows per page:</span>
            <RowsPerPageSelect
              value={reservationsPerPage}
              onChange={(e) => {
                setReservationsPerPage(Number(e.target.value));
                setCurrentPage(1); // Reset to first page when rows per page changes
              }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={20}>20</option>
            </RowsPerPageSelect>
          </div>
          <div>
            <PageButton onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>
              Previous
            </PageButton>
            <PageButton
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === Math.ceil(reservations.length / reservationsPerPage)}
            >
              Next
            </PageButton>
          </div>
        </PaginationContainer>
      </TableWrapper>

      {statusModalOpen && selectedReservation && (
        <ModalContainer>
          <Modal>
            <h3>Confirm Payment for {selectedReservation.name}</h3>
            <label>
              <FileInput
                type="file"
                accept="image/*"
                onChange={handleFileChange}
              />
            </label>
            <button
              onClick={handleStatusChange}
              disabled={uploading || !selectedFile}
            >
              Confirm
            </button>
            <button onClick={closeModal}>Close</button>
          </Modal>
        </ModalContainer>
      )}
    </ReservationsContainer>
  );
};

export default MyReservations;
